import React, { useState } from 'react'
import { GridColDef } from '@mui/x-data-grid'
import { GridItem } from '../gridItems'
import { IDashboard } from '../../../../domain/interfaces/IDashboard'
import EdifyIconButton from '../../buttons/EdifyIconButton/EdifyIconButton'
// import { toggleFavoriteDashboard } from '../../../../data/data'
import { PinActiveIcon } from '../../../../core/components/icons/PinActiveIcon'
import { PinIcon } from '../../../../core/components/icons/PinIcon'

import { Box } from '@mui/material'
import { ErrorToast } from '../../../../core/utils/toast-notifications/ToastNotifications'
import { dateToMMDDYY } from '../../../../core/core'
import { EdifyButton } from '../../buttons'
import { toggleDefaultDashboard, toggleFavoriteDashboard } from '../../../../data/data'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'

interface Props {
  favorited?: boolean
  isDefault: boolean
  id: string
  domoLink: string
  cb: (id: string) => void
  navigate: (id: string) => void
  getFavoriteCount: () => number
}

const FavoriteDashboardIcon: React.FC<Props> = ({ favorited, id, cb, getFavoriteCount, isDefault }) => {
  const FAVORITED_DASHBOARDS_MAX = 10
  const [loading, setLoading] = useState<boolean>(false)

  const toggleFavorite = async (e: any, getFavoriteCount: () => number) => {
    e.stopPropagation()
    if (isDefault) {
      ErrorToast({ title: 'You can not unfavorite your default dashboard.' })
      return
    }
    if (getFavoriteCount() >= FAVORITED_DASHBOARDS_MAX && !favorited) {
      ErrorToast({
        title: `You can only favorite up to ${FAVORITED_DASHBOARDS_MAX} dashboards.`,
      })
      return
    }
    setLoading(true)
    const res = await toggleFavoriteDashboard(id, favorited!)
    setLoading(false)
    if (res.isRight()) {
      cb(id)
    } else {
      ErrorToast({ title: 'Error saving as favorite.' })
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flex: 1,
        marginRight: '12px',
      }}
    >
      <EdifyIconButton
        onClick={e => toggleFavorite(e, getFavoriteCount)}
        disabled={loading}
        loading={loading}
        sx={{ border: 'none' }}
        title={favorited ? 'Remove from favorites' : 'Add to favorites'}
      >
        {favorited && !loading ? <PinActiveIcon /> : <PinIcon />}
      </EdifyIconButton>
    </Box>
  )
}

const DefaultDashboardIcon: React.FC<Props> = ({ isDefault, id, cb, favorited }) => {
  const [loading, setLoading] = useState<boolean>(false)

  const toggleDefault = async (e: any) => {
    e.stopPropagation()
    // Implementation for setting isDefault dashboard
    setLoading(true)
    const res = await toggleDefaultDashboard(id)
    setLoading(false)
    if (res.isRight()) {
      if (!favorited) {
        await toggleFavoriteDashboard(id, false)
      }
      cb(id)
    } else {
      ErrorToast({ title: 'Error setting as isDefault.' })
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flex: 1,
        marginRight: isDefault ? '0px' : '0px',
      }}
    >
      <EdifyButton
        noBackground
        textStyle={{
          ml: isDefault ? -4 : 0,
          pl: 0,
          color: isDefault ? '#2EBF43' : '#0A77FF',
          '&:hover': {
            color: isDefault ? '#2EBF43' : '#0A77FF',
          },
        }}
        onClick={e => toggleDefault(e)}
        disabled={loading}
        loading={loading}
        sx={{ border: 'none', p: 0 }}
        icon={
          isDefault ? (
            <CheckOutlinedIcon
              sx={{
                color: '#2EBF43',
                fontSize: '18px',
                ml: '-16px',
                fontWeight: 700,
              }}
            />
          ) : (
            <></>
          )
        }
        title={isDefault ? 'Default' : 'Select'}
      />
    </Box>
  )
}

export const dashboard_columns: GridColDef[] = [
  {
    field: 'name',
    renderHeader: () => <GridItem text='Name' />,
    flex: 1,
    renderCell: ({ row }) => <GridItem text={row.name} />,
  },

  {
    field: 'updateAt',
    renderHeader: () => <GridItem text='Last updated' />,
    width: 160,
    renderCell: ({ row }) => <GridItem text={dateToMMDDYY(row.updateAt)} />,
  },
  {
    field: 'favorite',
    width: 160,
    renderHeader: () => <GridItem text='Favorite' />,
    renderCell: ({ row }) => (
      <FavoriteDashboardIcon
        favorited={row.favorited}
        isDefault={row.isDefault}
        id={row.id}
        domoLink={row.domoLink}
        cb={row.updateFavoriteDashboardUI}
        navigate={row.navigate}
        getFavoriteCount={row.getFavoriteCount}
      />
    ),
  },
  {
    field: 'actions',
    width: 160,
    renderHeader: () => <GridItem text='Default' />,
    renderCell: ({ row }) => (
      <DefaultDashboardIcon
        isDefault={row.isDefault}
        id={row.id}
        domoLink={row.domoLink}
        cb={row.updateDefaultDashboardUI}
        navigate={row.navigate}
        favorited={row.isFavorited}
        getFavoriteCount={row.getFavoriteCount}
      />
    ),
  },
]

export const normalizeDashboardsForDataGrid = (
  dashboards: IDashboard[],
  updateFavoriteDashboardUI: (id: string) => void,
  updateDefaultDashboardUI: (id: string) => void,
  navigate: (url: string) => void,
  getFavoriteCount: () => void,
) => {
  if (!dashboards) return []
  return dashboards.map((dashboard: IDashboard) => {
    return {
      id: dashboard.id,
      name: dashboard.name,
      updatedAt: dashboard.updatedAt,
      favorited: dashboard.isFavorited,
      isDefault: dashboard.isDefault || false,
      updateFavoriteDashboardUI: updateFavoriteDashboardUI,
      updateDefaultDashboardUI: updateDefaultDashboardUI,
      navigate: navigate,
      getFavoriteCount: getFavoriteCount,
    }
  })
}
